/* Pipeline 172X: trial activation confirmation
   API: GET /me → AuthUser (confirms account state)
        GET /usage → UsageBalance { remaining: 150, state: 'full' }
   No credit card was required. Trial begins now.
   Preserved: authentication state, onboarding progress, safe way to leave and return. */

import { TrialUsageMeter } from "../components/trial/TrialComponents";
import { useEntitlements } from "../features/entitlements/useEntitlements";
import type { NavCallbacks } from "../types/auth";

type TrialActivatedPageProps = NavCallbacks;

export default function TrialActivatedPage({ goTo }: TrialActivatedPageProps) {
  const { balance, catalog, loading, error, retry } = useEntitlements();

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
      role="main"
      aria-labelledby="activation-heading"
    >
      {/* Card */}
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 20,
          padding: "40px 36px",
          boxShadow: "0 8px 40px rgba(26,58,92,0.08)",
          textAlign: "center",
        }}
      >
        {/* Success mark */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: "#f0fdf4",
            border: "2px solid #bbf7d0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
          aria-hidden="true"
        >
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
            <path
              d="M6 15.5L12 21.5L24 9"
              stroke="#15803d"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1
          id="activation-heading"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 800,
            fontSize: 22,
            color: "#111827",
            letterSpacing: "-0.03em",
            marginBottom: 8,
          }}
        >
          Your free trial is active
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 14,
            color: "#6b7280",
            marginBottom: 28,
            lineHeight: 1.55,
          }}
        >
          {loading
            ? "Loading your trial entitlement…"
            : `${catalog?.trialAllowance ?? balance?.total ?? 0} ${catalog?.trialUnit ?? "job evaluations"} available. No credit card required. Your saved work is retained even after the allowance is used.`}
        </p>

        {/* Usage meter */}
        <div style={{ marginBottom: 28 }}>
          {balance ? (
            <TrialUsageMeter balance={balance} />
          ) : (
            <div
              role={error ? "alert" : "status"}
              style={{
                padding: 16,
                borderRadius: 10,
                background: error ? "#fef2f2" : "#f9fafb",
                color: error ? "#b91c1c" : "#6b7280",
              }}
            >
              {error ?? "Loading usage…"}
              {error && (
                <button
                  type="button"
                  onClick={retry}
                  style={{
                    marginLeft: 12,
                    border: 0,
                    background: "transparent",
                    color: "#1a3a5c",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </div>

        {/* API annotation */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 9,
            padding: "10px 14px",
            marginBottom: 28,
            textAlign: "left",
          }}
        >
          <div
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#9ca3af",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            What happens next
          </div>
          {[
            "Complete your career profile and evidence bank",
            "Create your first discovery campaign",
            "Review matched roles in your inbox",
            "Inspect why each role matched or failed a hard filter",
          ].map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10.5,
                  color: "#1a3a5c",
                  fontWeight: 500,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                0{i + 1}
              </span>
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 12.5,
                  color: "#374151",
                  lineHeight: 1.5,
                }}
              >
                {s}
              </span>
            </div>
          ))}
        </div>

        {/* Primary CTA */}
        <button
          onClick={() => goTo("onboarding")}
          disabled={!balance}
          style={{
            width: "100%",
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            color: "#fff",
            background: "#1a3a5c",
            border: "none",
            borderRadius: 10,
            padding: "14px 20px",
            cursor: balance ? "pointer" : "not-allowed",
            opacity: balance ? 1 : 0.6,
            marginBottom: 12,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1e4a7a")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1a3a5c")}
        >
          Begin profile setup →
        </button>

        {/* Safe exit */}
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#9ca3af",
            lineHeight: 1.5,
          }}
        >
          You can leave and return any time. Your trial and saved work will be
          here.
        </p>
      </div>

      {/* Brand */}
      <div
        style={{ marginTop: 28, display: "flex", alignItems: "center", gap: 8 }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: "#1a3a5c",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: 9,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            172
          </span>
        </div>
        <span
          style={{
            fontFamily: "DM Mono, monospace",
            fontSize: 11,
            color: "#9ca3af",
            letterSpacing: "0.04em",
          }}
        >
          172X | Pipeline by Mastylo Labs
        </span>
      </div>
    </div>
  );
}
